import type { ActionContext } from 'vuex';
import type { UpdatePageParam, CancelEventParam, CreateEventParam } from './types';
import { mutations as MUTATION, actions as ACTION } from './types';
import Api from '@/services/Api';
import { base, shopmeta } from '@/services/Endpoint';
import type { State } from './state';
import getParameterByName from '@/app/utils/getParameterByName';
import { checkKeyValid } from '@/app/utils/checkRoleApply';
import { getEmbedAppUrl } from '@/utils/getEmbedAppUrl';
import { BUILDER_OPEN_NEW_TAB } from '@/constants/feature-flag-key';
import { postMessageToParent } from '@/app/utils/helpers';

const actions = {
  [ACTION.SAVE_PAGE]({ state, commit }: ActionContext<State, any>, data: UpdatePageParam) {
    return new Promise(async (resolve, reject) => {
      try {
        const res = await Api.base.post(base.savePage, data);

        if (res?.data?.status == 'success') {
          commit(MUTATION.UPDATE_PAGE_INFO, data);
          resolve(res.data);
        } else {
          reject(res?.data);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        reject(error);
      }
    });
  },
  [ACTION.CREATE_PAGE_EVENT]({ state, commit }: ActionContext<State, any>, data: CreateEventParam) {
    return new Promise(async (resolve, reject) => {
      try {
        const res = await Api.base.post(base.event(data.type, data.id, data.event), data.body);

        if (res?.data?.status == 'success') {
          commit(MUTATION.CREATE_PAGE_EVENT, data);
          resolve(res.data);
        } else {
          reject(res?.data);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        reject(error);
      }
    });
  },
  [ACTION.CANCEL_PAGE_EVENT]({ state, commit }: ActionContext<State, any>, data: CancelEventParam) {
    return new Promise(async (resolve, reject) => {
      try {
        const res = await Api.base.delete(base.event(data.type, data.id, data.event));

        if (res?.data?.status == 'success') {
          commit(MUTATION.CANCEL_PAGE_EVENT, data);
          resolve(res.data);
        } else {
          reject(res?.data);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        reject(error);
      }
    });
  },
  [ACTION.UPDATE_SHOP_META]({ state, commit }: ActionContext<State, any>, data: any) {
    return new Promise(async (resolve, reject) => {
      try {
        const res = await Api.base.post(base.updateShopMeta, {
          key: 'onboarding-global-style',
          metas: data,
        });

        if (res?.data?.status == 'success') {
          resolve(res.data);
        } else {
          reject(res?.data);
        }
      } catch (error) {
        reject(error);
      }
    });
  },
  [ACTION.GET_SHOP_META]({ commit }: any) {
    return new Promise(async (resolve, reject) => {
      try {
        const payload = { keys: ['active-font', 'theme-font', 'installed-font', 'onboarding-global-style', 'meter'] };
        const res = await Api.base.post(shopmeta.batch, payload);

        if (res?.data?.message == 'success') {
          const metaData = res && res.data.data;

          metaData.map((item: { key: string; value: string }) => {
            if (item.key === 'onboarding-global-style') {
              commit(MUTATION.SHOP_META, JSON.parse(item.value));
            } else if (item.key.includes('-font')) {
              const fontMeta = {
                key: item.key,
                value: item.value,
              };
              commit(MUTATION.SET_FONT_META, fontMeta);
            } else if (item.key.includes('meter')) {
              commit(MUTATION.SET_SHOP_METER, JSON.parse(item.value));
            }
          });
          resolve(res.data);
        } else {
          reject(res?.data);
        }
      } catch (error) {
        reject(error);
      }
    });
  },
  [ACTION.UPDATE_SHOP_META_USE_KEY](
    { state, commit }: ActionContext<State, any>,
    payload: { key: string; metas: any },
  ) {
    return new Promise(async (resolve, reject) => {
      try {
        const res = await Api.base.post(base.updateShopMeta, {
          key: payload.key,
          metas: payload.metas,
        });

        if (res?.data?.status == 'success') {
          resolve(res.data);
        } else {
          reject(res?.data);
        }
      } catch (error) {
        reject(error);
      }
    });
  },
  [ACTION.UPDATE_LAST_USED]({ commit }: ActionContext<State, any>, lastUsedAt: string) {
    return new Promise(async (resolve, reject) => {
      try {
        const payload = {
          key: 'last-gem-meter-used-at',
          value: lastUsedAt,
        };
        const res = await Api.base.post(shopmeta.base, payload);
        if (res.data.status === 'success') {
          commit(MUTATION.UPDATE_LAST_GEM_METER_USED_AT, lastUsedAt);
          resolve(res.data);
        } else {
          reject(res.data);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        reject(error);
      }
    });
  },
  [ACTION.GET_PAGE_INFO]({ commit }: ActionContext<State, any>) {
    return new Promise(async (resolve, reject) => {
      try {
        let pageID = getParameterByName('id');
        const pageType = getParameterByName('type');
        const shopifyDomain = getParameterByName('shop') ?? '';
        if (!pageID) {
          pageID = '51460767821';
        }
        const res = await Api.base.get(base.pageInfo(pageID), { params: { type: pageType } });
        if (res.data.status == 'success') {
          commit(MUTATION.COMMIT_PAGE_INFO, res.data.data);
          resolve(res.data.data);
        } else {
          if (checkKeyValid(BUILDER_OPEN_NEW_TAB)) {
            const url = getEmbedAppUrl(shopifyDomain);
            if (window.top) {
              window.top.location.href = url;
            } else {
              window.location.href = url;
            }
            return;
          }
          postMessageToParent({ type: 'windowCloseBuilder' });
          reject('login failed');
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        reject('login failed');
      }
    });
  },
};

export default actions;
